import { useMemo, useRef } from 'react';
import QueryKeys from '@constants/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { DayHeaderContentArg } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import { isMobile } from 'react-device-detect';
import { addMonths, format } from 'date-fns';
import * as workerApi from '@company/services/worker/api';
import { useCompanyContext } from '@company/state/companyContext';
import { AvailabilityType } from '@company/types/workerAvailability.type';
import { IWorkerData } from '@th-types/worker.type';
import DATE_FORMATS from '@constants/dateFormat';
import useOnScreen from '@hooks/useIntersectionObserver';
import { processCalendarAvailabilities } from '@utils/DateUtils';
import { FIVE_MINUTES } from '@constants/various';
import renderEventContent from './renderEventContent';

import './style.css';

interface CalendarViewParams {
  worker: IWorkerData;
}

export default function CalendarView({ worker }: CalendarViewParams) {
  const { id: companyId } = useCompanyContext();

  const ref = useRef(null);
  const isOnScreen = useOnScreen(ref);

  const queryKey = `${QueryKeys.WORKER_AVAILABILITIES}_${worker.id}`;

  const { data: rawAvailabilities } = useQuery({
    queryKey: [queryKey],
    staleTime: FIVE_MINUTES,
    refetchOnMount: false,
    enabled: isOnScreen,
    queryFn: async () => {
      const today = new Date();
      const futureDate = addMonths(today, 6);

      const formattedFromDate = format(today, DATE_FORMATS.DATE_FORMAT);
      const formattedEndDate = format(futureDate, DATE_FORMATS.DATE_FORMAT);

      const { workerAvailabilities } = await workerApi.fetchWorkerAvailability(
        worker.id,
        formattedFromDate,
        formattedEndDate
      );

      // Filter out ADDRESS_CHANGE types
      const filtered = workerAvailabilities.filter(
        (availability) => availability.type !== AvailabilityType.ADDRESS_CHANGE
      );

      return filtered;
    },
  });

  // Chunk and convert events when raw data changes
  const calendarEvents = useMemo(
    () =>
      rawAvailabilities
        ? processCalendarAvailabilities(rawAvailabilities, companyId)
        : [],
    [rawAvailabilities, companyId]
  );

  const renderDayHeaderContent = (args: DayHeaderContentArg) => {
    const { date, text, view, isToday } = args;
    const { type } = view;
    const formattedDate = format(date, DATE_FORMATS.CALENDAR_DAY_FORMAT);
    const dateParts = formattedDate.split(' ');
    const formattedContent = `${dateParts[0]} <span class="fc-${
      isToday ? 'today' : 'other-day'
    }-header">${dateParts[1]}</span>`;
    return type === 'dayGridMonth' ? text : { html: formattedContent };
  };

  return (
    <div ref={ref} className="mt-4">
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, bootstrap5Plugin]}
        headerToolbar={{
          left: isMobile ? 'prev' : 'prev next',
          center: 'title',
          right: isMobile ? 'next' : 'timeGridWeek,dayGridMonth',
        }}
        allDaySlot
        height="auto"
        initialView={isMobile ? 'dayGridDay' : 'dayGridMonth'}
        selectable
        selectMirror
        dayMaxEvents
        themeSystem="bootstrap5"
        titleFormat={{ year: 'numeric', month: 'long' }}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'narrow',
          separator: '-',
        }}
        slotLabelFormat={{
          hour: 'numeric',
          minute: 'numeric',
          meridiem: 'narrow',
        }}
        views={{
          dayGridMonth: {
            dayMaxEvents: 2,
          },
        }}
        dayHeaderContent={renderDayHeaderContent}
        displayEventEnd
        events={calendarEvents}
        eventContent={(event) => renderEventContent(event)}
      />
    </div>
  );
}
